import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import "normalize.css/normalize.css";
import ElementPlus from "element-plus";
import "element-plus/dist/index.css";
// @ts-expect-error - locale file has no types
import de from "element-plus/dist/locale/de.mjs";

import App from "./App.vue";
import router from "@/router";
import i18n from "./lang";

import "@/styles/index.scss";
import "@/styles/tiptap-content.css";

import moment from "moment";
// @ts-expect-error - locale file has no types
import "moment/dist/locale/de";

// FontAwesome
import "@/utils/fontawesome";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Directives
import {
  Clipboard,
  Waves,
  ifIsUser,
  ifIsContact,
  ifIsAdmin,
  ifIsManager,
  ifIsSpaceAdmin,
  ifIsNotUser,
  ifIsNotContact,
  ifIsNotManager,
  hasContactsApp,
  hasTasksApp,
  hasProtocolsApp,
  hasMailApp,
} from "@/directive";

// Composables (replacing filters)
// Filters are no longer supported in Vue 3, use composables instead

// Disable console.log in production
if (import.meta.env.PROD) {
  console.log = function () {};
}

// Suppress ResizeObserver loop notifications — a non-breaking cosmetic Safari
// issue triggered by Element Plus tabs dynamically adding/removing tab panes.
window.addEventListener(
  "error",
  (event) => {
    if (
      event.message ===
      "ResizeObserver loop completed with undelivered notifications"
    ) {
      event.stopImmediatePropagation();
    }
  },
  true,
);

// Set moment locale
moment.locale("de");

// Sign-in first (#294): outside the public pages the browser goes to Keycloak and comes back with
// tokens; the app and its first navigation only start once that is settled.
import { initKeycloak } from "@/services/keycloak";
import { removeLegacyAuthData } from "@/utils/storage";

// Create Vue app
const app = createApp(App);

// Create and configure Pinia (installed with the other plugins once Keycloak is ready, below)
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// Register Element Plus icons globally
// Element Plus icons are imported on-demand in components
// If needed globally, they should be registered individually

// Register FontAwesome
app.component("FaIcon", FontAwesomeIcon);

// Register global directives
app.directive("Clipboard", Clipboard);
app.directive("Waves", Waves);
app.directive("ifIsUser", ifIsUser);
app.directive("ifIsContact", ifIsContact);
app.directive("ifIsAdmin", ifIsAdmin);
app.directive("ifIsManager", ifIsManager);
app.directive("ifIsSpaceAdmin", ifIsSpaceAdmin);
app.directive("ifIsNotUser", ifIsNotUser);
app.directive("ifIsNotContact", ifIsNotContact);
app.directive("ifIsNotManager", ifIsNotManager);
app.directive("hasContactsApp", hasContactsApp);
app.directive("hasTasksApp", hasTasksApp);
app.directive("hasProtocolsApp", hasProtocolsApp);
app.directive("hasMailApp", hasMailApp);

// Import permission control (after router is set up)
import { pathNoAuth } from "./permission";

// Import error logging
import "./utils/error-log";

// Once the first navigation has settled, warm all view chunks in the background so
// a later deploy can't break the currently-open session with a stale-chunk error.
import { prefetchViews } from "@/utils/prefetch-views";

removeLegacyAuthData();
initKeycloak(pathNoAuth(window.location.pathname)).then(() => {
  // Plugins installed only now: the router's first navigation runs the auth guard.
  app.use(pinia);
  app.use(router);
  app.use(i18n);
  app.use(ElementPlus, {
    locale: de,
  });
  app.mount("#app");
  router.isReady().then(() => {
    prefetchViews();
  });
});

export default app;
